import { useEffect, useState, type FormEvent } from 'react'
import type { UserSummary, UserRole } from '@ultrix/shared'
import { useStore } from '../store'
import { listUsers, createUser, setUserPassword, patchUser, resetUser2fa, deleteUser } from '../api'

const TOTP_NOTE = 'This admin has no 2FA yet and cannot log in until you run: npm run enroll-admin -- <username>'

export function Users() {
  const me = useStore((s) => s.user)
  const [users, setUsers] = useState<UserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [newName, setNewName] = useState('')
  const [newPass, setNewPass] = useState('')
  const [newRole, setNewRole] = useState<UserRole>('operator')

  async function refetch() {
    setLoading(true); setError(null)
    try { setUsers(await listUsers()) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { refetch() }, [])

  function guard<T>(fn: () => Promise<T>) {
    return async () => {
      try { await fn(); setNotice(null); await refetch() }
      catch (e) { alert(e instanceof Error ? e.message : String(e)) }
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault()
    try {
      const res = await createUser({ username: newName, password: newPass, role: newRole })
      setNewName(''); setNewPass(''); setNewRole('operator')
      setNotice(res.totpEnrollmentRequired ? TOTP_NOTE : null)
      await refetch()
    } catch (e) { alert(e instanceof Error ? e.message : String(e)) }
  }

  async function onSetPassword(u: UserSummary) {
    const pw = prompt(`New password for ${u.username} (min 8 chars):`)
    if (!pw) return
    await guard(() => setUserPassword(u.id, pw))()
  }
  async function onToggleRole(u: UserSummary) {
    const role: UserRole = u.role === 'admin' ? 'operator' : 'admin'
    if (!confirm(`Change ${u.username} to ${role}?`)) return
    try {
      const res = await patchUser(u.id, { role })
      setNotice(res.totpEnrollmentRequired ? TOTP_NOTE : null)
      await refetch()
    } catch (e) { alert(e instanceof Error ? e.message : String(e)) }
  }
  async function onToggleDisabled(u: UserSummary) {
    await guard(() => patchUser(u.id, { disabled: !u.disabled }))()
  }
  async function onToggle2fa(u: UserSummary, isSelf: boolean) {
    let totpCode: string | undefined
    if (isSelf) {
      // The server requires a current code to change your own 2FA.
      const code = prompt(u.totpRequired
        ? 'Turn your 2FA off? You will log in with just your password.\nEnter your current 6-digit authenticator code to confirm:'
        : 'Turn your 2FA back on?\nEnter your current 6-digit authenticator code to confirm:')
      if (!code) return
      totpCode = code.trim()
    } else {
      const msg = u.totpRequired
        ? `Turn 2FA off for ${u.username}? They will log in with just their password.`
        : `Turn 2FA back on for ${u.username}?`
      if (!confirm(msg)) return
    }
    try {
      const res = await patchUser(u.id, { totpRequired: !u.totpRequired, totpCode })
      setNotice(res.totpEnrollmentRequired ? TOTP_NOTE : null)
      await refetch()
    } catch (e) { alert(e instanceof Error ? e.message : String(e)) }
  }
  async function onReset2fa(u: UserSummary) {
    if (!confirm(`Reset 2FA for ${u.username}? They will need re-enrollment.`)) return
    try {
      const res = await resetUser2fa(u.id)
      setNotice(res.totpEnrollmentRequired ? TOTP_NOTE : null)
      await refetch()
    } catch (e) { alert(e instanceof Error ? e.message : String(e)) }
  }
  async function onDelete(u: UserSummary) {
    if (!confirm(`Delete ${u.username}? This cannot be undone.`)) return
    await guard(() => deleteUser(u.id))()
  }

  return (
    <div className="p-4 overflow-auto">
      <h2 className="font-bold text-lg mb-3">Users</h2>

      {notice && (
        <div className="mb-3 p-3 rounded-md border border-amber-500 bg-amber-500/10 text-sm">{notice}</div>
      )}
      {error && <div className="mb-3 text-err text-sm">{error}</div>}

      <form onSubmit={onCreate} className="mb-5 flex flex-wrap gap-2 items-end">
        <label className="flex flex-col text-xs">Username
          <input className="mt-1 px-2 py-1 rounded bg-panel border border-border" value={newName}
            onChange={(e) => setNewName(e.target.value)} required minLength={3} />
        </label>
        <label className="flex flex-col text-xs">Password
          <input className="mt-1 px-2 py-1 rounded bg-panel border border-border" type="password" value={newPass}
            onChange={(e) => setNewPass(e.target.value)} required minLength={8} />
        </label>
        <label className="flex flex-col text-xs">Role
          <select className="mt-1 px-2 py-1 rounded bg-panel border border-border" value={newRole}
            onChange={(e) => setNewRole(e.target.value as UserRole)}>
            <option value="operator">operator</option>
            <option value="admin">admin</option>
          </select>
        </label>
        <button className="px-3 py-1.5 rounded-md border border-accent bg-accent/20 text-sm" type="submit">Add user</button>
      </form>

      {loading ? <div className="text-sub">Loading…</div> : (
        <table className="w-full text-sm border-collapse">
          <thead><tr className="text-left text-sub border-b border-border">
            <th className="py-2">Username</th><th>Role</th><th>2FA</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.username === me?.username
              return (
                <tr key={u.id} className="border-b border-border/50">
                  <td className="py-2">{u.username}{isSelf && <span className="text-sub"> (you)</span>}</td>
                  <td>{u.role}</td>
                  <td>{u.role !== 'admin' ? '—'
                    : !u.totpRequired ? <span className="text-amber-500">off</span>
                    : u.totpEnrolled ? '✓' : '⚠ none'}</td>
                  <td>{u.disabled ? <span className="text-err">disabled</span> : 'active'}</td>
                  <td className="flex flex-wrap gap-1 py-1">
                    <button className="px-2 py-1 rounded border border-border text-xs" onClick={() => onSetPassword(u)}>Password</button>
                    <button className="px-2 py-1 rounded border border-border text-xs" disabled={isSelf} onClick={() => onToggleRole(u)}>
                      {u.role === 'admin' ? 'Make operator' : 'Make admin'}</button>
                    <button className="px-2 py-1 rounded border border-border text-xs" disabled={isSelf} onClick={() => onToggleDisabled(u)}>
                      {u.disabled ? 'Enable' : 'Disable'}</button>
                    {u.role === 'admin' && <button className="px-2 py-1 rounded border border-border text-xs" disabled={isSelf && !u.totpEnrolled} onClick={() => onToggle2fa(u, isSelf)}>
                      {u.totpRequired ? 'Turn 2FA off' : 'Turn 2FA on'}</button>}
                    {u.role === 'admin' && <button className="px-2 py-1 rounded border border-border text-xs" disabled={isSelf} onClick={() => onReset2fa(u)}>Reset 2FA</button>}
                    <button className="px-2 py-1 rounded border border-err text-err text-xs" disabled={isSelf} onClick={() => onDelete(u)}>Delete</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
